import React, { useEffect } from "react";
import { BrowserRouter, Routes, Route, useLocation } from "react-router-dom";
import { Toaster } from "@/components/ui/sonner";
import { LangProvider, useLang } from "@/context/LangContext";
import { Navbar } from "@/components/Navbar";
import { Hero } from "@/components/Hero";
import { Specialties } from "@/components/Specialties";
import { FisioQuiro } from "@/components/FisioQuiro";
import { AlternativeTherapies } from "@/components/AlternativeTherapies";
import { Estetica } from "@/components/Estetica";
import { About } from "@/components/About";
import { Team } from "@/components/Team";
import { Testimonials } from "@/components/Testimonials";
import { Booking } from "@/components/Booking";
import { Faq } from "@/components/Faq";
import { Blog, BlogIndex, BlogArticle } from "@/components/Blog";
import { Contact } from "@/components/Contact";
import { Footer } from "@/components/Footer";
import { WhatsappFab } from "@/components/WhatsappFab";
import { AdminPage } from "@/components/AdminPage";
import { ServicePage } from "@/components/ServicePage";
import Quiz from "@/components/Quiz";
import QuizDeportistas from "@/components/QuizDeportistas";
import Quiz50Mas from "@/components/Quiz50Mas";
import QuizPostural from "@/components/QuizPostural";
import { X } from "lucide-react";
import { JsonLd } from "@/seo/JsonLd";
import { websiteSchema, businessRatingSchema } from "@/seo/schemaData";
import "@/App.css";

const SeoHead = () => {
  const { lang } = useLang();
  const loc = useLocation();
  useEffect(() => {
    document.documentElement.lang = lang === "es" ? "es-MX" : "en";
    if (loc.pathname === "/") {
      const titleEs = "Terapia Física Pachuca | Quiropráctico Pachuca · TERAFIQUIRO Wellness Center · Fisio Omar";
      const titleEn = "Physical Therapy in Pachuca | Chiropractor Pachuca · TERAFIQUIRO Wellness Center · Fisio Omar";
      document.title = lang === "es" ? titleEs : titleEn;
      // Also sync meta description for runtime SEO consistency
      const metaDesc = document.querySelector('meta[name="description"]');
      if (metaDesc && lang === "es") {
        metaDesc.setAttribute("content", "Terapia Física en Pachuca y Quiropráctico Pachuca con Fisio Omar. ⭐ 10+ años de experiencia. Tratamos ciática, hernias discales, lumbalgia, dolor de rodilla y lesiones deportivas. Rehabilitación, ozonoterapia, punción seca, ventosas y ajustes quiroprácticos. Agenda por WhatsApp o en línea. Colonia Centro, Pachuca de Soto, Hidalgo.");
      }
    }
  }, [lang, loc.pathname]);
  return null;
};

// Scrolls to #hash on route change (so /#blog works when navigating from another route)
const ScrollToHash = () => {
  const loc = useLocation();
  // Desactivar la restauración automática de scroll del navegador para que
  // al ingresar al sitio siempre se posicione hasta arriba del Home.
  useEffect(() => {
    if (typeof window !== "undefined" && "scrollRestoration" in window.history) {
      window.history.scrollRestoration = "manual";
    }
    // Scroll al tope en la carga inicial (sin importar a dónde apuntaba el navegador)
    window.scrollTo(0, 0);
  }, []);

  useEffect(() => {
    if (loc.hash) {
      const id = loc.hash.replace("#", "");
      // wait for sections to mount before scrolling
      const t = setTimeout(() => {
        const el = document.getElementById(id);
        if (el) el.scrollIntoView({ behavior: "smooth", block: "start" });
      }, 150);
      return () => clearTimeout(t);
    } else {
      window.scrollTo({ top: 0, left: 0 });
    }
  }, [loc.pathname, loc.hash]);
  return null;
};

const Landing = () => (
  <LangProvider>
    <SeoHead />
    <JsonLd id="landing" data={[websiteSchema(), businessRatingSchema()].filter(Boolean)} />
    <div className="App min-h-screen bg-white">
      <Navbar />
      <main data-testid="main-content">
        <Hero />
        <Specialties />
        <FisioQuiro />
        <Booking sectionId="booking-fisio" tKey="bookingFisio" services={["terapia_fisica","rehabilitacion","quiropractica"]} defaultService="terapia_fisica" testidPrefix="booking-fisio" variant="default" whatsappNumber="527712141634" />
        <AlternativeTherapies />
        <Booking sectionId="booking-alt" tKey="bookingAlt" services={["terapias_alternativas"]} defaultService="terapias_alternativas" testidPrefix="booking-alt" variant="alt" whatsappNumber="527711610609" />
        <Estetica />
        <Booking sectionId="booking-estetica" tKey="bookingEstetica" services={["estetica"]} defaultService="estetica" testidPrefix="booking-estetica" variant="estetica" whatsappNumber="527711290073" />
        <About />
        <Team />
        <Testimonials />
        <Faq />
        <Blog />
        <Contact />
      </main>
      <Footer />
      <WhatsappFab />
    </div>
  </LangProvider>
);

const ServiceWrapped = () => (
  <LangProvider>
    <ServicePage />
    <WhatsappFab />
  </LangProvider>
);

const BlogPageWrapped = ({ children }) => (
  <LangProvider>
    <Navbar />
    <main className="pt-16">{children}</main>
    <Footer />
    <WhatsappFab />
  </LangProvider>
);

const QuizPageWrapped = () => (
  <LangProvider>
    <Navbar />
    <Quiz />
    <Footer />
    <WhatsappFab />
  </LangProvider>
);

const QuizDeportistasWrapped = () => (
  <LangProvider>
    <PopupShell>
      <QuizDeportistas />
    </PopupShell>
  </LangProvider>
);

const Quiz50MasWrapped = () => (
  <LangProvider>
    <PopupShell>
      <Quiz50Mas />
    </PopupShell>
  </LangProvider>
);

const QuizPosturalWrapped = () => (
  <LangProvider>
    <PopupShell>
      <QuizPostural />
    </PopupShell>
  </LangProvider>
);

// Standalone full-screen shell for secret quizzes — no nav, no footer, no menu.
// Includes a floating "Cerrar" button (X) that returns to the home page.
const PopupShell = ({ children }) => (
  <div className="relative min-h-screen bg-white" data-testid="popup-shell">
    <a
      href="/"
      className="fixed top-4 right-4 z-50 inline-flex items-center gap-1.5 rounded-full bg-white border border-slate-200 shadow-lg px-4 py-2 text-xs font-bold text-slate-700 hover:bg-slate-50 hover:text-slate-900 transition-colors"
      data-testid="popup-close"
      aria-label="Cerrar y volver al inicio"
    >
      <X className="h-4 w-4" /> Cerrar
    </a>
    <div className="pt-2">{children}</div>
  </div>
);

function App() {
  return (
    <BrowserRouter>
      <ScrollToHash />
      <Routes>
        <Route path="/admin/*" element={<AdminPage />} />
        <Route path="/servicios/:slug" element={<ServiceWrapped />} />
        <Route path="/quiz" element={<QuizPageWrapped />} />
        <Route path="/quiz/deportistas" element={<QuizDeportistasWrapped />} />
        <Route path="/quiz/50-mas" element={<Quiz50MasWrapped />} />
        <Route path="/quiz/postural" element={<QuizPosturalWrapped />} />
        <Route path="/blog" element={<BlogPageWrapped><BlogIndex /></BlogPageWrapped>} />
        <Route path="/blog/:slug" element={<BlogPageWrapped><BlogArticle /></BlogPageWrapped>} />
        <Route path="/" element={<Landing />} />
      </Routes>
      <Toaster position="top-right" richColors closeButton />
    </BrowserRouter>
  );
}

export default App;
